// frontend/app/components/lessonPlans/present/PresentTextBlocks.js
//
// Prose on a wall. The workspace draws rich_text and bullet_list inside a
// bordered card, which is right for a document you scroll and wrong for a slide
// — a box around every paragraph turns a lesson into a form. Here the type size
// and the space around it do the separating, and the only block that keeps a
// border is the callout, where the border is the meaning.
//
// teacher_tip does not appear in this file at all: it is routed into the notes
// panel by presentRules, because on a mirrored screen a tip naming the
// misconception to watch for is read by the class the moment it is drawn.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getCalloutVariant } from "../lessonPlanTheme";
import { PresentBody, PresentHeading } from "./PresentSurface";

export const PresentRichTextBlock = ({ block, colors, type }) => (
  <View>
    {block.title ? (
      <PresentHeading type={type} colors={colors}>
        {block.title}
      </PresentHeading>
    ) : null}
    <PresentBody
      type={type}
      colors={colors}
      color={colors.textPrimary}
      style={block.title ? styles.spacedBody : null}
    >
      {block.text}
    </PresentBody>
  </View>
);

export const PresentBulletListBlock = ({ block, colors, type }) => {
  const items = Array.isArray(block.items) ? block.items : [];
  return (
    <View>
      {block.title ? (
        <PresentHeading type={type} colors={colors}>
          {block.title}
        </PresentHeading>
      ) : null}
      <View style={[styles.bulletList, block.title ? styles.spacedBody : null]}>
        {items.map((item, index) => (
          <View key={`${block.id}-item-${index}`} style={styles.bulletRow}>
            <View
              style={[
                styles.bulletDot,
                {
                  backgroundColor: colors.accentPrimary,
                  marginTop: Math.round(type.body.fontSize * 0.55),
                },
              ]}
            />
            <PresentBody
              type={type}
              colors={colors}
              color={colors.textPrimary}
              style={styles.bulletText}
            >
              {item}
            </PresentBody>
          </View>
        ))}
      </View>
    </View>
  );
};

/** The one block that keeps its box — a callout is a box by definition. */
export const PresentCalloutBlock = ({ block, colors, type, isDarkMode }) => {
  const variant = getCalloutVariant(block.variant, colors, isDarkMode);
  return (
    <View
      style={[
        styles.callout,
        { backgroundColor: variant.bg, borderColor: variant.border },
      ]}
    >
      <View style={styles.calloutHeader}>
        <MaterialCommunityIcons
          name={variant.icon}
          size={Math.round(type.body.fontSize * 1.4)}
          color={variant.accent}
        />
        {block.title ? (
          <Text
            style={[styles.calloutTitle, type.eyebrow, { color: variant.accent }]}
          >
            {block.title}
          </Text>
        ) : null}
      </View>
      <PresentBody type={type} colors={colors} color={colors.textPrimary}>
        {block.text}
      </PresentBody>
    </View>
  );
};

/**
 * ai_prompt. Each question is pressable: it opens the Saras rail and asks that
 * question, so what is on the wall is what the teacher presses. The first shape
 * of this printed the questions here and put a tappable copy inside the panel,
 * which made a teacher read a question, open a panel, and find it again.
 *
 * The answer still appears in the rail rather than growing in place. An answer
 * unfolding inside the slide would push the rest of the step off screen in the
 * middle of a lesson, and the class is reading the step.
 *
 * `onAskQuestion` is optional. Without it the questions stay plain text — they
 * are worth reading aloud whether or not anything can answer them.
 */
export const PresentAiPromptBlock = ({ block, colors, type, onAskQuestion, t }) => {
  const translate = t || ((value) => value);
  const questions = Array.isArray(block.questions) ? block.questions : [];

  return (
    <View
      style={[
        styles.aiCard,
        { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder },
      ]}
    >
      <View style={styles.calloutHeader}>
        <MaterialCommunityIcons
          name="robot-happy-outline"
          size={Math.round(type.body.fontSize * 1.4)}
          color={colors.accentPrimary}
        />
        <Text
          style={[styles.calloutTitle, type.eyebrow, { color: colors.accentPrimary }]}
        >
          {block.title || translate("Ask the room")}
        </Text>
      </View>

      {questions.map((question, index) => {
        // Pressable only where something can answer. Without a handler these
        // stay exactly what they were — questions worth reading aloud.
        const Row = onAskQuestion ? TouchableOpacity : View;
        const rowProps = onAskQuestion
          ? {
              onPress: () => onAskQuestion(question),
              accessibilityRole: "button",
              accessibilityLabel: `${translate("Ask Saras")}: ${question}`,
            }
          : {};

        return (
          <Row
            key={`${block.id}-q-${index}`}
            style={styles.aiQuestionRow}
            {...rowProps}
          >
            <Text
              style={[
                styles.aiQuestionIndex,
                type.caption,
                { color: colors.accentPrimary },
              ]}
            >
              {index + 1}
            </Text>
            <PresentBody
              type={type}
              colors={colors}
              color={colors.textPrimary}
              style={styles.aiQuestionText}
            >
              {question}
            </PresentBody>
            {onAskQuestion ? (
              <MaterialCommunityIcons
                name="chevron-right"
                size={Math.round(type.body.fontSize * 1.2)}
                color={colors.accentPrimary}
              />
            ) : null}
          </Row>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  spacedBody: { marginTop: 18 },

  bulletList: { gap: 16 },
  bulletRow: { flexDirection: "row", alignItems: "flex-start" },
  bulletDot: { width: 10, height: 10, borderRadius: 5, marginRight: 18 },
  bulletText: { flex: 1 },

  callout: {
    borderRadius: 18,
    borderWidth: 2,
    padding: 24,
  },
  calloutHeader: { flexDirection: "row", alignItems: "center", marginBottom: 14 },
  calloutTitle: {
    fontWeight: "800",
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginLeft: 10,
  },

  aiCard: {
    borderRadius: 18,
    borderWidth: 2,
    padding: 24,
  },
  aiQuestionRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 14 },
  aiQuestionIndex: { fontWeight: "800", marginRight: 14, marginTop: 4 },
  aiQuestionText: { flex: 1 },
});
